import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ThemeContext } from './context'
import type { Theme, ThemeValue } from './context'

const STORAGE_KEY = 'bella.theme'

// Kept in sync with the meta theme-color in index.html and --surface in
// index.css. This is the one place that value needs to change at runtime --
// the CSS variable it otherwise tracks isn't readable by the OS chrome.
const THEME_COLOR: Record<Theme, string> = {
  light: '#ffffff',
  dark: '#0b0b0f',
}

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark'
}

/**
 * Dark is the default, not a fallback: it is what a visitor with no stored
 * preference gets. Matches the inline script in index.html, which applies the
 * same default to <html> before React (or even the stylesheet) loads, so
 * there is nothing for this initial state to visibly correct.
 */
function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isTheme(stored)) return stored
  } catch {
    // Private mode, or storage disabled. Fall through to the default.
  }
  return 'dark'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(initialTheme)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLOR[theme])
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Not being able to remember the choice is survivable; failing is not.
    }
  }, [])

  const value = useMemo<ThemeValue>(() => ({ theme, setTheme }), [theme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
